/**
 * Lightweight, privacy-friendly analytics client.
 *
 * Sends anonymous usage events to the `analytics` Netlify Function, which stores
 * them in Netlify Blobs. This lets us see how many people (Nostr or not) use
 * BCHPAY.Cash, how many payments complete, and how many invoices get created —
 * without collecting any personally identifying information.
 *
 * All calls are fire-and-forget: they never block the UI and never throw.
 */

const ENDPOINT = '/.netlify/functions/analytics';

export type AnalyticsEvent =
  | { type: 'pageview'; path?: string; ref?: string }
  | {
      type: 'payment';
      fiatAmount: number;
      bchAmount: number;
      tipAmount?: number;
      currency: string;
      offline?: boolean;
      itemCount?: number;
    }
  | {
      type: 'invoice';
      total: number;
      currency: string;
      itemCount?: number;
    };

/**
 * Send an analytics event. Uses `navigator.sendBeacon` when available so the
 * request survives page navigation; falls back to a keepalive fetch. Silently
 * does nothing on failure — analytics must never break the app.
 */
export function track(event: AnalyticsEvent): void {
  try {
    const body = JSON.stringify(event);

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }

    // Fallback: keepalive fetch (also used in non-beacon environments).
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* ignore — analytics is best-effort */
    });
  } catch {
    /* ignore — analytics is best-effort */
  }
}

/** Convenience helper for a page view. */
export function trackPageview(path: string): void {
  const ref =
    typeof document !== 'undefined' && document.referrer && !document.referrer.includes(location.host)
      ? document.referrer
      : '';
  track({ type: 'pageview', path, ref });
}
